import { FormInput, InfoBox, TabsNavigation } from 'components/molecules';
import { TopBar } from 'components/organisms';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CURRENCY_ID,
  IStatement,
  IStatementConsistency,
  STATEMENT_STATUS,
} from 'services/statements/types';
import { II18nextPrefix } from 'types/components/components.interface';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { IFormProps } from '../types';
import Errors from './tabs/Errors';
import ExchangeRates from './tabs/ExchangeRates';
import Rows from './tabs/Rows';
import SummaryRows from './tabs/SummaryRows';
import UnrelatedRows from './tabs/UnrelatedRows';

export enum statementsTabs {
  exchangeRates,
  rows,
  unrelatedRows,
  summaryRows,
  errors,
}

export const initialStatement: IStatement = {
  id: '',
  salesChannelId: '',
  settlementPeriodId: '',
  providerId: '',
  providerName: '',
  statementStatus: STATEMENT_STATUS.unknown,
  defaultCurrency: CURRENCY_ID.PLN,
  consistency: null as IStatementConsistency | null,
  errors: [],
  defaultTotalValue: 0,
  statementFileId: '',
  statementFilePath: '',
  settlementPeriodName: { name: '', year: -1 },
  salesChannelName: '',
  exchangeRates: [],
  hasUnrelatedRows: false,
  createdAt: '',
  editedAt: null,
};

interface ExchangeRatesFormProps
  extends FormikConfig<IStatement>,
    IFormProps,
    II18nextPrefix {}

interface ExchangeRatesFormComponent extends React.FC<ExchangeRatesFormProps> {
  getRouterTabs?: (
    disabledChildren?: string[]
  ) => { path: string; element: JSX.Element }[];
}

const currencySuffix: Record<number, string> = {
  0: '',
  1: 'PLN',
  2: 'USD',
  3: 'EUR',
  4: 'CHF',
  5: 'GBP',
};

const ExchangeRatesForm: ExchangeRatesFormComponent = ({
  initialValues = initialStatement,
  onSubmit = () => {},
  topbarContent,
  status,
  customBackItem,
  backPath,
}) => {
  const { t } = useTranslation();

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      initialStatus={status}
      onSubmit={onSubmit}
      validateOnChange={true}
      validateOnBlur={true}>
      {({ values }) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              path={backPath ?? 'statements'}
              title={getSettlementPeriodName(values.settlementPeriodName)}
              topbarContent={topbarContent}>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Items columns={1}>
                  <FormInput.Text
                    name='salesChannelName'
                    label={`${t('input.label.salesChannel')}`}
                  />
                  <FormInput.Text
                    name='providerName'
                    label={`${t('input.label.provider')}`}
                  />
                  <FormInput.Number
                    name='defaultTotalValue'
                    label={`${t('input.label.totalValue')}`}
                    suffix={` ${currencySuffix[values.defaultCurrency] ?? ''}`}
                  />
                </InfoBox.Items>
              </InfoBox>

              <TabsNavigation
                tabs={[
                  { value: statementsTabs.exchangeRates, name: 'exchangeRate' },
                  { value: statementsTabs.rows, name: 'rows' },
                  { value: statementsTabs.summaryRows, name: 'summaryRows' },
                  ...(values.hasUnrelatedRows
                    ? [
                        {
                          value: statementsTabs.unrelatedRows,
                          name: 'unrelatedRows',
                        },
                      ]
                    : []),
                  ...(values.errors?.length
                    ? [{ value: statementsTabs.errors, name: 'errors' }]
                    : []),
                ]}
                disabled={
                  values.statementStatus !== STATEMENT_STATUS.done
                    ? [statementsTabs.summaryRows, statementsTabs.rows]
                    : undefined
                }
                i18nextPrefix={'statements'}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

ExchangeRatesForm.getRouterTabs = (disabledChildren: string[] = []) =>
  [
    { path: 'exchangeRate', element: <ExchangeRates /> },
    { path: 'unrelatedRows', element: <UnrelatedRows /> },
    { path: 'summaryRows', element: <SummaryRows /> },
    { path: 'rows', element: <Rows /> },
    { path: 'errors', element: <Errors /> },
  ].filter(child => !disabledChildren.includes(child.path));

export default ExchangeRatesForm;
